import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Trash2, Plus, MessageSquareWarning, FileText } from "lucide-react";

// Staff-side manager for an agency's cross-project "Shared Documents" — rooted at that
// agency's own Drive `Shared Documents` folder (Wave 2), NOT tied to any one project. For
// material an agency needs across every one of its productions (master agreement, rate
// card). Same publish/proxy/fail-closed-hide rules as the per-project Document Room in
// AgencyWorkspaceManager.jsx, hitting the agency-network routes instead of the project ones.
const DOC_TYPES = ["SOW", "Invoice", "Other"];
const DOC_STATUSES = ["Draft", "Sent", "Approved"];
const needsRelink = (d) => !(d.file_id || d.external_url);
const fieldCls = "w-full rounded-md border border-hairline bg-paper px-2.5 py-1.5 text-xs text-ink";

export default function AgencySharedDocuments({ agencyRemoteId }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState("");
  const [loadErr, setLoadErr] = useState(false);
  const [docFormTarget, setDocFormTarget] = useState(null); // null | "new" | doc object being replaced

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/v1/build/agency-network/${agencyRemoteId}/documents`);
      setDocs(r.data.documents || []);
      setLoadErr(false);
    } catch { setLoadErr(true); }
  }, [agencyRemoteId]);

  useEffect(() => { reload(); }, [reload]);

  const upsertDoc = async (payload) => {
    setBusy("doc");
    try {
      await api.post(`/v1/build/agency-network/${agencyRemoteId}/documents`, { audience: "Partner Agency", ...payload });
      await reload();
      return true;
    } catch { toast.error("Couldn't save document"); return false; }
    finally { setBusy(""); }
  };

  const deleteDoc = async (id) => {
    setBusy(`del-doc-${id}`);
    try { await api.delete(`/v1/build/agency-network/${agencyRemoteId}/documents/${id}`); await reload(); }
    catch { toast.error("Couldn't delete"); } finally { setBusy(""); }
  };

  const previewDoc = async (d) => {
    try {
      const r = await api.get(`/v1/build/agency-network/${agencyRemoteId}/documents/${d.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Couldn't open this document"); }
  };

  return (
    <div className="rounded-md border border-hairline p-3.5" data-testid={`agency-shared-documents-${agencyRemoteId}`}>
      <div className="flex items-center justify-between">
        <label className="block text-[11px] uppercase tracking-wider text-faint">Shared Documents (all projects)</label>
        <button onClick={() => setDocFormTarget((t) => (t ? null : "new"))} data-testid="agency-shared-doc-add-toggle"
          className="inline-flex items-center gap-1 text-[11px] text-brand-dim hover:underline"><Plus className="h-3 w-3" /> Publish a document</button>
      </div>
      {loadErr && (
        <div className="mt-2 flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" data-testid="agency-shared-documents-error">
          <span>Couldn't load shared documents. This is a load error, not an empty list.</span>
          <button onClick={reload} className="font-medium underline">Retry</button>
        </div>
      )}
      {docs.length === 0 && !docFormTarget && !loadErr && (
        <div className="mt-1.5 text-[11px] text-faint">None yet. Publish a Drive file or a review link — visible to the agency across every project, not just one.</div>
      )}
      <div className="mt-2 space-y-1.5">
        {docs.map((d) => {
          const relink = needsRelink(d);
          return (
            <div key={d.id} className="flex items-center justify-between gap-2 rounded border border-hairline bg-paper px-2.5 py-1.5" data-testid={`agency-shared-doc-row-${d.id}`}>
              <div className="min-w-0">
                <div className="truncate text-xs text-ink"><FileText className="mr-1 inline h-3 w-3 text-faint" />{d.name || "Untitled"} <span className="text-[10px] text-faint">· {d.doc_type} v{d.version} · {d.status}</span></div>
                {relink ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800" data-testid={`agency-shared-doc-needs-relink-${d.id}`}>
                    <MessageSquareWarning className="h-3 w-3" /> Needs re-link — pasted link, not proxied
                  </span>
                ) : d.source === "external_link" ? (
                  <a href={d.external_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-brand-dim hover:underline" data-testid={`agency-shared-doc-external-link-${d.id}`}>{d.external_url}</a>
                ) : (
                  <button onClick={() => previewDoc(d)} data-testid={`agency-shared-doc-preview-${d.id}`} className="text-[10px] text-brand-dim hover:underline">Preview (streamed, same as agency sees)</button>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {relink && <button onClick={() => setDocFormTarget(d)} disabled={!!docFormTarget}
                  data-testid={`agency-shared-doc-relink-btn-${d.id}`} className="rounded-full border border-amber-300 px-2 py-0.5 text-[10px] text-amber-800 hover:bg-amber-50">Replace</button>}
                <button onClick={() => deleteDoc(d.id)} disabled={busy === `del-doc-${d.id}`}
                  data-testid={`agency-shared-doc-delete-${d.id}`} className="text-faint hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>
      {docFormTarget && (
        <SharedDocForm agencyRemoteId={agencyRemoteId} doc={docFormTarget === "new" ? null : docFormTarget} busy={busy === "doc"}
          onSave={async (p) => { if (await upsertDoc(p)) setDocFormTarget(null); }} onCancel={() => setDocFormTarget(null)} />
      )}
    </div>
  );
}

function SharedDocForm({ agencyRemoteId, doc, busy, onSave, onCancel }) {
  const [docType, setDocType] = useState(doc?.doc_type || "SOW");
  const [name, setName] = useState(doc?.name || "");
  const [mode, setMode] = useState(doc?.source === "external_link" ? "link" : "drive");
  const [fileId, setFileId] = useState("");
  const [fileName, setFileName] = useState("");
  const [url, setUrl] = useState(doc?.external_url || "");
  const [version, setVersion] = useState(String(doc?.version || 1));
  const [status, setStatus] = useState(doc?.status || "Sent");
  const [files, setFiles] = useState(null);
  const [filesErr, setFilesErr] = useState("");

  const loadFiles = useCallback(async () => {
    setFiles("loading");
    try {
      const r = await api.get(`/v1/build/agency-network/${agencyRemoteId}/documents/drive-files`);
      setFiles(r.data.items || []);
      setFilesErr("");
    } catch (e) {
      setFiles([]);
      setFilesErr(e?.response?.data?.detail?.code === "agency_shared_documents_folder_not_found"
        ? "This agency has no Shared Documents Drive folder yet — create the Drive workspace above first."
        : "Couldn't load this agency's Drive files.");
    }
  }, [agencyRemoteId]);

  useEffect(() => { if (mode === "drive" && files === null) loadFiles(); }, [mode, files, loadFiles]);

  const canSave = name.trim() && (mode === "drive" ? !!fileId : !!url.trim());

  return (
    <div className="mt-3 space-y-2 rounded-md border border-hairline bg-paper p-3" data-testid="agency-shared-doc-form">
      {doc && <div className="text-[11px] font-medium text-amber-800">Replacing a legacy pasted link — choose a Drive file or paste a review link below.</div>}
      <div className="grid grid-cols-2 gap-2">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="agency-shared-doc-type-select" className={fieldCls}>
          {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Document name" data-testid="agency-shared-doc-name-input" className={fieldCls} />
      </div>
      <div className="flex gap-1 rounded-md bg-black/5 p-0.5 text-[11px]">
        <button type="button" onClick={() => setMode("drive")} data-testid="agency-shared-doc-mode-drive-btn"
          className={`flex-1 rounded px-2 py-1 ${mode === "drive" ? "bg-white shadow-sm text-ink" : "text-faint"}`}>Choose from Drive</button>
        <button type="button" onClick={() => setMode("link")} data-testid="agency-shared-doc-mode-link-btn"
          className={`flex-1 rounded px-2 py-1 ${mode === "link" ? "bg-white shadow-sm text-ink" : "text-faint"}`}>External review link</button>
      </div>
      {mode === "drive" ? (
        <div className="max-h-56 overflow-y-auto rounded border border-hairline" data-testid="agency-shared-doc-drive-picker">
          {files === "loading" && <div className="p-2 text-[11px] text-faint">Loading this agency's Shared Documents files…</div>}
          {filesErr && <div className="p-2 text-[11px] text-red-600" data-testid="agency-shared-doc-drive-picker-error">{filesErr}</div>}
          {Array.isArray(files) && files.length === 0 && !filesErr && <div className="p-2 text-[11px] text-faint">No files found (folders named "_Internal (NJM only)" are always excluded).</div>}
          {Array.isArray(files) && files.map((f) => (
            <button key={f.id} type="button" disabled={f.too_large || f.already_registered}
              onClick={() => { setFileId(f.id); setFileName(f.name); if (!name.trim()) setName(f.name); }}
              data-testid={`agency-shared-doc-drive-file-${f.id}`}
              title={f.too_large ? "Too large for the portal (500MB cap) — use a review link instead" : f.already_registered ? "Already published" : ""}
              className={`flex w-full items-center justify-between gap-2 border-b border-hairline px-2 py-1.5 text-left text-[11px] last:border-0 ${fileId === f.id ? "bg-brand-dim/10" : "hover:bg-black/[0.02]"} ${(f.too_large || f.already_registered) ? "cursor-not-allowed opacity-40" : ""}`}>
              <span className="truncate">{f.path}{f.name}</span>
              {f.too_large ? <span className="shrink-0 text-amber-700">Too large</span> : f.already_registered ? <span className="shrink-0 text-faint">Published</span> : null}
            </button>
          ))}
          {fileId && <div className="border-t border-hairline bg-green-50 px-2 py-1 text-[11px] text-green-800" data-testid="agency-shared-doc-drive-selected">Selected: {fileName || fileId}</div>}
        </div>
      ) : (
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Review link (Frame.io, Vimeo, YouTube…)" data-testid="agency-shared-doc-url-input" className={fieldCls} />
      )}
      <div className="grid grid-cols-2 gap-2">
        <input type="number" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="Version" data-testid="agency-shared-doc-version-input" className={fieldCls} />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="agency-shared-doc-status-select" className={fieldCls}>
          {DOC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button disabled={busy || !canSave} data-testid="agency-shared-doc-save-btn"
          onClick={() => onSave({ id: doc?.id, doc_type: docType, title: name.trim(), version: version === "" ? 1 : Number(version), status,
            ...(mode === "drive" ? { file_id: fileId } : { external_url: url.trim() }) })}
          className="rounded-md bg-ink px-3 py-1.5 text-xs text-white disabled:opacity-50">{busy ? "Publishing…" : doc ? "Save" : "Publish document"}</button>
        <button onClick={onCancel} className="rounded-md border border-hairline px-3 py-1.5 text-xs text-faint">Cancel</button>
      </div>
    </div>
  );
}
